import cx from 'classnames';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { DesignSystemContextProvider } from '../DesignSystemContext';

export interface DialogProps
  extends React.DialogHTMLAttributes<
    Omit<HTMLDialogElement, 'open' | 'hidden'>
  > {
  isOpen: boolean;
  onClose: () => void;
  isBackdropTransparent?: boolean;
  fullScreen?: boolean;
  /**
   * @deprecated Dynamic class `tw-w-${value}` isn't generated by Tailwind's
   * JIT compiler, so the width never applies. Set width on the inner content
   * via `className` instead (e.g. `<Modal className="tw-w-[480px]">`).
   */
  deprecated_width?: string;
  /**
   * Effective keyboard overlap (px) to avoid. When > 0 the dialog shifts up by
   * half this amount so it stays centered in the still-visible area above the
   * keyboard. Default 0 (no shift) — opt-in per caller so other modals are
   * unaffected.
   *
   * NOTE: pass the overlap measured in the dialog's own (layout-viewport)
   * coordinate space. On iOS the visual viewport can pan (`offsetTop`), so a raw
   * keyboard height over-shifts; callers should subtract `offsetTop` first (see
   * `useVisualViewportInsets`).
   */
  keyboardOffset?: number;
  /**
   * Only attached to the underlying `<dialog>` while `isOpen` — see the
   * destructuring below for why this can't rely on the excess-property
   * exemption `data-*` attributes normally get when spread via `rest`.
   */
  'data-testid'?: string;
}

/**
 * Upper bound on the fade-out before `close()` is forced. Must stay longer than
 * the opacity transition below (`tw-transition-opacity` = 150ms) so it never
 * truncates a real fade, and short enough that a dialog which produced no
 * transition at all cannot keep holding the top layer.
 */
const CLOSE_FALLBACK_MS = 500;

/**
 * React wrapper around html <dialog> element
 */
export function Dialog({
  isOpen,
  onClose,
  isBackdropTransparent,
  className,
  children,
  fullScreen,
  deprecated_width,
  keyboardOffset = 0,
  style,
  'data-testid': dataTestId,
  ...rest
}: DialogProps) {
  const [isShowing, setIsShowing] = useState(true);

  const [dialogElement, setDialogElement] = useState<HTMLDialogElement | null>(
    null,
  );

  // run open/close commands on the dialog
  useEffect(() => {
    if (isOpen && dialogElement && !dialogElement.open) {
      dialogElement.showModal();
    }

    // this is distinct from `isOpen` so that it updates in the next render
    // which lets the opacity transition work
    setIsShowing(isOpen);

    if (!isOpen && dialogElement) {
      const closeAfterTransitionEnded = (e: TransitionEvent) => {
        if (e.target === dialogElement && !e.pseudoElement) {
          dialogElement.close();
        }
      };

      dialogElement.addEventListener(
        'transitionend',
        closeAfterTransitionEnded,
      );

      // `transitionend` is not guaranteed to arrive, and it is the only thing
      // that closes the element. Closing before the browser has committed the
      // opening fade leaves the computed opacity at 0 the whole time, so no
      // transition runs and no event ever fires; an interrupted or throttled
      // fade can also end in `transitioncancel` instead. A <dialog> left `open`
      // at opacity 0 is invisible yet still holds the top layer, so its
      // backdrop swallows every pointer event on the page — reads still work,
      // which makes the wedge silent until the next click. Force the close once
      // the fade can only be over.
      const closeFallback = setTimeout(() => {
        dialogElement.close();
      }, CLOSE_FALLBACK_MS);

      return () => {
        clearTimeout(closeFallback);

        dialogElement.removeEventListener(
          'transitionend',
          closeAfterTransitionEnded,
        );
      };
    }

    return undefined;
  }, [isOpen, dialogElement]);

  return createPortal(
    <dialog
      {...rest}
      // The portal keeps this element mounted in `document.body` for the
      // component's whole lifetime, dropping only the `open` attribute after
      // the close transition ends (see the effect above). A caller that can
      // have a second, closed copy mounted nearby (e.g. two modal-triggering
      // features on the same page) would otherwise leave a stale, closed
      // element answering to the same testid. Attaching it only while open
      // makes `getByTestId` resolve uniquely without every caller needing to
      // know about this portal-persistence quirk.
      data-testid={isOpen ? dataTestId : undefined}
      ref={setDialogElement}
      style={{
        ...style,
        transform:
          keyboardOffset > 0
            ? `translateY(-${Math.round(keyboardOffset / 2)}px)`
            : style?.transform,
      }}
      className={cx(
        deprecated_width && `tw-w-${deprecated_width}`,
        /* override browser default stylesheet */
        'tw-overflow-visible',
        'tw-bg-transparent',
        'tw-p-0',
        'tw-max-h-none',
        'tw-max-w-none',
        fullScreen && 'tw-w-full',
        fullScreen && 'tw-h-full',
        /* background */
        isBackdropTransparent
          ? 'backdrop:tw-bg-transparent'
          : 'backdrop:tw-bg-black/20',
        /* fade in on open and fade out on close */
        !isShowing && 'tw-opacity-0',
        'tw-transition-opacity',
        !isBackdropTransparent && !isShowing && 'backdrop:tw-opacity-0',
        !isBackdropTransparent && 'backdrop:tw-transition-opacity',
      )}
      onCancel={(e) => {
        // close on press escape
        e.preventDefault();
        onClose();
      }}
      onPointerDown={(e) => {
        // close on click outside
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <DesignSystemContextProvider portalContainer={dialogElement}>
        <div className={className}>{children}</div>
      </DesignSystemContextProvider>
    </dialog>,
    document.body,
  );
}
